import { useEffect, useMemo, useState } from "react";
import { AlignLeft, Bell, CalendarDays, MapPin, Palette, Plus, Tag, X } from "lucide-react";
import { useCreateEvent, useUpdateEvent } from "../../api/hooks";
import type { CalEvent } from "../../api/types";
import { DEFAULT_COLOR, EVENT_COLORS } from "../../lib/colors";
import { addDays, diffDays } from "../../lib/date";
import { KINDS, draftToInput, withStart, type Draft } from "../../lib/draft";
import { describeRule, presetIndex, recurrencePresets } from "../../lib/recurrence";
import { anchorOf, useUi, type Anchor } from "../../state/ui";
import { ColorPicker } from "../ui/ColorPicker";
import { DateSelect } from "../ui/DateSelect";
import { Popover } from "../ui/Popover";
import { TimeSelect } from "../ui/TimeSelect";
import { RecurrenceDialog } from "./RecurrenceDialog";
import { reminderLabel } from "./labels";
import "./overlays.css";

const TIMED_REMINDERS = [0, 5, 10, 15, 30, 60, 120, 1440, 2880, 10080];
const ALLDAY_REMINDERS = [0, 1440, 2880, 10080];

/** Full-page event editor ("Mais opções"). */
export function EventEditor({ draft: initial, event }: { draft: Draft; event?: CalEvent }) {
  const ui = useUi();
  const create = useCreateEvent();
  const update = useUpdateEvent();
  const [draft, setDraft] = useState(initial);
  const [customRule, setCustomRule] = useState(false);
  const [colorMenu, setColorMenu] = useState<Anchor | null>(null);
  const requestId = useMemo(() => crypto.randomUUID(), []);
  const settings = ui.me.settings;
  const calendar = ui.calendars.find((c) => c.id === draft.calendarId);
  const presets = recurrencePresets(draft.startDate);
  const presetIdx = presetIndex(draft.recurrence, draft.startDate);

  const close = () => ui.setOverlay(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.querySelector(".rec-dialog, .menu")) close();
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const save = async () => {
    const input = draftToInput(draft);
    if (!event) {
      create.mutate(
        { ...input, clientRequestId: requestId },
        { onSuccess: () => ui.showToast("Evento salvo"), onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`) }
      );
      close();
      return;
    }
    const scope = await ui.askScope(event, "edit");
    if (!scope) return;
    update.mutate(
      { event, scope, changes: input },
      { onSuccess: () => ui.showToast("Evento salvo"), onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`) }
    );
    close();
  };

  const reminderChoices = draft.allDay ? ALLDAY_REMINDERS : TIMED_REMINDERS;
  const addReminder = () => {
    const next = reminderChoices.find((m) => !draft.reminders.includes(m));
    if (next !== undefined && draft.reminders.length < 5) setDraft({ ...draft, reminders: [...draft.reminders, next] });
  };

  const multiDayTimed = !draft.allDay && draft.endDate !== draft.startDate;

  return (
    <div className="editor" role="dialog" aria-label={event ? "Editar evento" : "Novo evento"}>
      <header className="editor-head">
        <button className="icon-btn" aria-label="Fechar" onClick={close}>
          <X size={22} />
        </button>
        <input
          autoFocus={!event}
          className="field editor-title"
          placeholder="Adicionar título"
          aria-label="Título"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && void save()}
        />
        <button className="btn primary" onClick={() => void save()}>
          Salvar
        </button>
      </header>
      <div className="kind-tabs editor-kinds" role="tablist" aria-label="Tipo">
        {KINDS.map(({ kind, label }) => (
          <button key={kind} type="button" role="tab" aria-selected={draft.kind === kind} className={`kind-tab${draft.kind === kind ? " active" : ""}`} onClick={() => setDraft({ ...draft, kind })}>
            {label}
          </button>
        ))}
      </div>

      <div className="editor-when">
        <DateSelect value={draft.startDate} today={ui.today} weekStart={settings.weekStart} ariaLabel="Data de início" onChange={(d) => setDraft(withStart(draft, d, draft.startMin))} />
        {!draft.allDay && <TimeSelect ariaLabel="Hora de início" value={draft.startMin} onChange={(m) => setDraft(withStart(draft, draft.startDate, m))} />}
        <span className="editor-ate">até</span>
        {!draft.allDay && (
          <TimeSelect
            ariaLabel="Hora de término"
            value={draft.endMin + diffDays(draft.startDate, draft.endDate) * 1440}
            startMin={multiDayTimed ? undefined : draft.startMin}
            onChange={(m) => setDraft({ ...draft, endDate: multiDayTimed ? draft.endDate : addDays(draft.startDate, Math.floor(m / 1440)), endMin: m % 1440 })}
          />
        )}
        {(draft.allDay || multiDayTimed) && (
          <DateSelect
            value={draft.endDate}
            today={ui.today}
            weekStart={settings.weekStart}
            ariaLabel="Data de término"
            onChange={(d) => setDraft({ ...draft, endDate: d < draft.startDate ? draft.startDate : d })}
          />
        )}
      </div>
      <div className="editor-when">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.allDay}
            onChange={(e) =>
              setDraft({
                ...draft,
                allDay: e.target.checked,
                endDate: draft.startDate,
                reminders: e.target.checked ? settings.defaultAllDayReminders : settings.defaultReminders
              })
            }
          />
          Dia inteiro
        </label>
        <select
          className="chip-select"
          aria-label="Repetição"
          value={presetIdx >= 0 ? String(presetIdx) : "current"}
          onChange={(e) => {
            if (e.target.value === "custom") setCustomRule(true);
            else if (e.target.value !== "current") setDraft({ ...draft, recurrence: presets[Number(e.target.value)].rule });
          }}
        >
          {presets.map((p, i) => (
            <option key={p.label} value={String(i)}>
              {p.label}
            </option>
          ))}
          {presetIdx < 0 && draft.recurrence && <option value="current">{describeRule(draft.recurrence, draft.startDate)}</option>}
          <option value="custom">Personalizar...</option>
        </select>
      </div>

      <div className="editor-tabs">
        <span className="editor-tab active">Detalhes do evento</span>
      </div>

      <div className="editor-body">
        <div className="editor-row">
          <MapPin size={20} className="details-icon" />
          <input className="field filled" placeholder="Adicionar local" value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
        </div>

        <div className="editor-row top">
          <Bell size={20} className="details-icon" />
          <div className="editor-reminders">
            {draft.reminders.map((m, i) => (
              <div key={`${m}-${i}`} className="editor-reminder">
                <select
                  className="chip-select"
                  value={m}
                  aria-label="Notificação"
                  onChange={(e) => setDraft({ ...draft, reminders: draft.reminders.map((r, j) => (j === i ? Number(e.target.value) : r)) })}
                >
                  {[...new Set([...reminderChoices, m])].sort((a, b) => a - b).map((opt) => (
                    <option key={opt} value={opt}>
                      {reminderLabel(opt, draft.allDay)}
                    </option>
                  ))}
                </select>
                <button className="icon-btn sm" aria-label="Remover notificação" onClick={() => setDraft({ ...draft, reminders: draft.reminders.filter((_, j) => j !== i) })}>
                  <X size={16} />
                </button>
              </div>
            ))}
            {draft.reminders.length < 5 && (
              <button className="btn text" style={{ paddingLeft: 8 }} onClick={addReminder}>
                <Plus size={16} /> Adicionar notificação
              </button>
            )}
            <div className="editor-hint">
              Avisos por {[settings.notifyWhatsapp && "WhatsApp", settings.notifyPush && "notificação do navegador"].filter(Boolean).join(" e ") || "nenhum canal (veja Configurações)"}.
            </div>
          </div>
        </div>

        <div className="editor-row">
          <CalendarDays size={20} className="details-icon" />
          <select className="chip-select" aria-label="Agenda" value={draft.calendarId} onChange={(e) => setDraft({ ...draft, calendarId: e.target.value })}>
            {ui.calendars.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button className="color-btn" aria-label="Cor do evento" title="Cor do evento" onClick={(e) => setColorMenu(anchorOf(e.currentTarget))}>
            <span className="color-dot" style={{ background: draft.color ?? calendar?.color ?? DEFAULT_COLOR, width: 16, height: 16 }} />
            <Palette size={16} />
          </button>
        </div>

        <div className="editor-row">
          <Tag size={20} className="details-icon" />
          <select className="chip-select" aria-label="Categoria" value={draft.category ?? ""} onChange={(e) => setDraft({ ...draft, category: e.target.value || null })}>
            <option value="">Categoria automática</option>
            {ui.me.categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.emoji} {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="editor-row top">
          <AlignLeft size={20} className="details-icon" />
          <textarea className="field filled editor-desc" placeholder="Adicionar descrição" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </div>
      </div>

      {colorMenu && (
        <Popover anchor={colorMenu} placement="below" onClose={() => setColorMenu(null)} keepOpen>
          <ColorPicker
            colors={EVENT_COLORS}
            value={draft.color}
            calendarColor={calendar?.color}
            onChange={(hex) => {
              setDraft({ ...draft, color: hex });
              setColorMenu(null);
            }}
          />
        </Popover>
      )}
      {customRule && (
        <RecurrenceDialog
          start={draft.startDate}
          value={draft.recurrence}
          today={ui.today}
          weekStart={settings.weekStart}
          onCancel={() => setCustomRule(false)}
          onDone={(rule) => {
            setDraft({ ...draft, recurrence: rule });
            setCustomRule(false);
          }}
        />
      )}
    </div>
  );
}
